import { apiClient } from './api'

export interface Vehicle {
  id?: string
  tenant_id?: string
  partner_id?: string | null
  registration_number?: string | null
  type: 'TRUCK' | 'RIGID_TRUCK' | 'VAN' | 'TRAILER' | 'SEMI_TRAILER' | 'CONTAINER' | 'OTHER'
  depot_site_id?: string | null
  depot_address_id?: string | null
  vehicle_subtype?: 'porteur_2_essieux_12t' | 'porteur_2_essieux_18t' | 'porteur_3_essieux_26t' | 'tracteur_4x2' | 'tracteur_6x2' | 'semi_remorque_2_essieux_34t' | 'semi_remorque_3_essieux_44t' | 'remorque_2_essieux' | 'remorque_3_essieux' | 'fourgonnette_standard' | 'dry_20' | 'dry_40' | 'dry_45' | 'high_cube_40' | 'high_cube_45' | 'reefer_20' | 'reefer_40' | 'open_top_20' | 'open_top_40' | 'flat_rack_20' | 'flat_rack_40' | 'tank_20' | 'tank_40' | null
  container_type?: 'dry_20' | 'dry_40' | 'dry_45' | 'high_cube_40' | 'high_cube_45' | 'reefer_20' | 'reefer_40' | 'open_top_20' | 'open_top_40' | 'flat_rack_20' | 'flat_rack_40' | 'tank_20' | 'tank_40' | null
  brand?: string | null
  model?: string | null
  year?: number | null
  capacity_volume?: number | null
  capacity_weight?: number | null
  empty_weight?: number | null
  length?: number | null
  width?: number | null
  height?: number | null
  fuel_type?: 'diesel' | 'gasoline' | 'electric' | 'hybrid' | 'lpg' | 'cng' | null
  euro_emission_standard?: 'EURO_1' | 'EURO_2' | 'EURO_3' | 'EURO_4' | 'EURO_5' | 'EURO_6' | 'EURO_7' | null
  has_reefer_unit?: boolean | null
  reefer_temperature_range?: { min: number; max: number; unit?: 'C' | 'F' | 'K' } | null
  is_hazardous_compatible?: boolean | null
  hazardous_certifications?: string[] | null
  has_tail_lift?: boolean | null
  tail_lift_capacity?: number | null
  has_loading_ramp?: boolean | null
  is_overweight_permitted?: boolean | null
  max_weight_permitted?: number | null
  /** PTRA : masse maximale de l'ensemble roulant, utilisée pour un tracteur. */
  max_combination_weight?: number | null
  mass_contract_version?: string | null
  mass_provenance?: Record<string, unknown> | null
  total_mileage?: number | null
  fuel_consumption_average?: number | null
  maintenance_cost_per_km?: number | null
  breakdown_rate?: number | null
  availability_rate?: number | null
  average_rating?: number | null
  max_driving_distance_per_day?: number | null
  preferred_regions?: string[] | null
  restricted_regions?: string[] | null
  requires_special_license?: boolean | null
  required_license_type?: string | null
  hourly_cost?: number | null
  cost_per_km?: number | null
  daily_fixed_cost?: number | null
  special_equipment?: string[] | null
  has_gps_tracking?: boolean | null
  has_tachograph?: boolean | null
  has_video_surveillance?: boolean | null
  driver_ids?: object | null
  // Optimization constraints
  max_floor_pressure?: number | null
  pressure_cell_target_size?: number | null
  target_cg_x?: number | null
  target_cg_y?: number | null
  max_cg_deviation?: number | null
  axle_configuration?: {
    influenceRadius?: number
    kingPinPosition?: number | null
    fifthWheelPosition?: number | null
    fifthWheelMaxLoad?: number | null
    fifthWheelHeight?: number
    kingPinHeight?: number
    trackWidth?: number
    useDynamicStability?: boolean
    enforceAxleLimits?: boolean
    physicsType?: 'rigid_truck' | 'truck' | 'van' | 'tractor_semitrailer' | 'trailer' | 'container' | 'custom'
    region?: string
    axles?: Array<{
      id: string
      position: number
      maxWeight: number
      type?: 'steering' | 'drive' | 'trailer' | 'tag' | 'other'
      emptyWeight?: number
    }>
    axleGroups?: Array<{
      id: string
      type: 'tandem' | 'tridem' | 'single'
      axleIds: string[]
      maxWeight?: number
      autoDetected?: boolean
    }>
  } | null
  obstacles?: Array<{
    id: string
    name: string
    position: { x: number; y: number; z: number }
    dimensions: { length: number; width: number; height: number }
  }> | null
  fifth_wheel?: {
    position: number
    type: 'standard' | 'low' | 'high'
    maxLoad: number
  } | null
  constraintEngineConfig?: {
    targetCG?: {
      x?: number
      y?: number
      z?: number
    }
  } | null
  status?: 'active' | 'inactive' | 'maintenance' | 'out_of_service'
  // Champs de favoris
  favorite_rating?: number | null
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
}

export interface VehicleFleetSummary {
  total: number
  active: number
  reefer: number
  adr: number
  volume: { value: number | null; known: number }
  payload: { value: number | null; known: number; applicable: number }
}

export interface VehicleListResponse {
  data: Vehicle[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface VehicleMassPreview {
  schema_version: string
  kind?: 'tractor_semitrailer'
  payload_limit_kg: number | null
  verification: 'complete' | 'partial' | 'legacy' | 'unverified' | 'invalid' | 'not_applicable'
  issues: Array<{ code: string; message: string; fields?: string[] }>
  unverified_checks: string[]
  payload_source?: { field: string; equipment: string; status: string; formula?: string } | null
  gross_limit_kg?: number | null
  combination_gross_limit_kg?: number | null
  combination_payload_limit_kg?: number | null
}

export interface VehicleListParams { page?: number; pageSize?: number; search?: string; searchTypes?: string; searchSubtypes?: string; status?: string; favorites?: boolean; reefer?: boolean; adr?: boolean }

export const vehicleService = {
  getFleetSummary: async (): Promise<VehicleFleetSummary> => {
    const { data } = await apiClient.get<VehicleFleetSummary>('/api/internal/tms/v1/vehicles/summary')
    return data
  },

  getVehicles: async (params?: VehicleListParams): Promise<VehicleListResponse> => {
    const { data } = await apiClient.get<VehicleListResponse>('/api/internal/tms/v1/vehicles', { params })
    return data
  },

  getVehicle: async (id: string): Promise<Vehicle> => {
    const { data } = await apiClient.get<Vehicle>(`/api/internal/tms/v1/vehicles/${id}`)
    return data
  },

  createVehicle: async (vehicle: Omit<Vehicle, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<Vehicle> => {
    const { data } = await apiClient.post<Vehicle>('/api/internal/tms/v1/vehicles', vehicle)
    return data
  },

  updateVehicle: async (id: string, vehicle: Partial<Vehicle>): Promise<Vehicle> => {
    const { data } = await apiClient.put<Vehicle>(`/api/internal/tms/v1/vehicles/${id}`, vehicle)
    return data
  },

  previewMass: async (payload: { vehicle?: Partial<Vehicle>; trailer?: Partial<Vehicle>; tractor?: Partial<Vehicle> }): Promise<VehicleMassPreview> => {
    const { data } = await apiClient.post<VehicleMassPreview>('/api/internal/tms/v1/vehicles/mass-preview', payload)
    return data
  },

  deleteVehicle: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/internal/tms/v1/vehicles/${id}`)
  }
}
